import { useMutation } from '@tanstack/react-query';
import { toast } from '@/components/ui/use-toast';
import { apiClient } from '@/lib/api/client';
import { getDataOrThrowApiError, handleApiError } from '@/lib/api/error';
import { orpc } from '@/lib/api/orpc';
import { useTranslation } from '@/lib/i18n';
import { queryClient } from '@/lib/queryClient';
type Options = {
  softwareId: string;
  appUserId: string;
  /** 剥奪対象の LicenseProduct ID（アラート行の licenseAlert.licenseProductId） */
  licenseProductId: string;
  onSuccess?: () => void;
};

/**
 * AppUser からライセンスを剥奪するためのカスタムフック
 * （software alerts の要ライセンス剥奪（VIOLATED_LICENSE）アクション用。
 * ライセンスページの剥奪アクションと同じ provisioning 経路を使う）
 */
export default function useLicenseRevoke(options: Options) {
  const {
    i18n
  } = useTranslation();
  const {
    mutate,
    isPending
  } = useMutation({
    mutationFn: async () => {
      const res = await apiClient.POST('/v2/software/{softwareId}/app-users/{appUserId}/license/revoke', {
        params: {
          path: {
            softwareId: options.softwareId,
            appUserId: options.appUserId
          }
        },
        body: {
          licenseProductId: options.licenseProductId
        }
      });
      return getDataOrThrowApiError(res);
    },
    onSuccess: async () => {
      await Promise.all([queryClient.invalidateQueries({
        queryKey: orpc.software.alertsList.key()
      }), queryClient.invalidateQueries({
        queryKey: orpc.software.alertsCount.key()
      })]);
      options.onSuccess?.();
    },
    onError: error => {
      const {
        message
      } = handleApiError(error, {
        language: i18n.language
      });
      toast({
        description: message,
        variant: 'destructive'
      });
    }
  });
  return {
    mutate,
    isLoading: isPending
  };
}